/*
 * @Author: Ryan
 * @Date: 2025-12-25
 * @Description: How to use section for AI Avatar Generator landing page
 */
import Image from 'next/image';
import React from 'react';

interface StepData {
  number: number;
  title: string;
  description: string;
}

const steps: StepData[] = [
  {
    number: 1,
    title: 'Choose Your Avatar',
    description:
      "Pick from FalcoCut's diverse library of stock avatars, or upload a clear photo to create your own digital twin.",
  },
  {
    number: 2,
    title: 'Add Your Script',
    description:
      "Type or paste your text, or upload an audio file. FalcoCut's AI voice and dubbing will sync it naturally to your avatar.",
  },
  {
    number: 3,
    title: 'Select a Voice',
    description:
      'Use AI voices in multiple languages or clone your own voice for a personal touch.',
  },
  {
    number: 4,
    title: 'Generate Your Video',
    description:
      'In minutes, get a professional avatar video ready for social media, product demos, online courses, or internal training.',
  },
];

const StepItem: React.FC<StepData> = ({ number, title, description }) => (
  <div className="flex gap-4">
    {/* 步骤编号 */}
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center text-white font-semibold text-sm">
      {number}
    </div>
    {/* 步骤内容 */}
    <div className="flex flex-col gap-2">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          How to Use the FalcoCut AI Avatar Generator
        </h2>

        {/* 内容区域 */}
        <div className="flex flex-col pad:flex-row gap-8 pad:gap-12 sm:gap-16 items-center">
          {/* 左侧图片 */}
          <div className="w-full pad:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#F8F9FC]">
              <Image
                src="https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-how-to-use.png"
                alt="FalcoCut AI Avatar Generator Interface"
                width={560}
                height={480}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* 右侧步骤 */}
          <div className="w-full pad:w-1/2 flex flex-col gap-6 pad:gap-8">
            {steps.map((step) => (
              <StepItem key={step.number} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
